import * as React from 'react';
import { observer } from "mobx-react-lite";
import { Button, Checkbox, Dialog, DialogBody, DialogFooter, DialogHeader, Input, Typography } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { alertController } from "../../../Common/Controllers/AlertController";
import { userAccountController } from "../../../Common/Controllers/UserAccountController";
import { userAjaxController } from '../../../Common/Ajax/UserAjaxController';

export const UserSubpage = observer(() => {
  const navigate = useNavigate()
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const togglePasswordDialog = () => {
    setPasswordDialogOpen(!passwordDialogOpen);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
  };

  const onChangePasswordClick = async () => {
    setPasswordError(null);

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    try {
      const workingStockist = userAccountController.stockist;
      if (!workingStockist) return;

      await userAjaxController.changePassword({
        stockistId: workingStockist.stockistId,
        password: newPassword,
        confirmPassword: confirmPassword,
      });

      alertController.displayAlert(
        "Password changed successfully",
        null,
        "success",
        3000
      );

      setNewPassword("");
      setConfirmPassword("");
      togglePasswordDialog();
    } catch (error) {
      alertController.displayAlert(
        "Failed to change password",
        "Please try again or contact support.",
        "error",
        4000
      );
    }
  };

  useEffect(() => {
    if (!userAccountController.isUserAccountDataLoading && !userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in in order to view this page.",
        "warning",
        4000
      );
      navigate("/latest");
    }
  }, [userAccountController.isUserAccountDataLoading]);

  useEffect(() => {
    const initWorkingStockist = async () => {
      if (userAccountController.isUserAccountDataLoading) return;

      if (!userAccountController.isLoggedIn) {
        alertController.displayAlert(
          "Login required",
          "You have to be logged in in order to view this page.",
          "warning",
          4000
        );
        navigate("/latest");
        return;
      }

      await userAccountController.getStockist();
      userAccountController.setWorkingStockist();
    };

    void initWorkingStockist();

    return () => userAccountController.resetWorkingStockist();
  }, [
    userAccountController.isUserAccountDataLoading,
    userAccountController.isLoggedIn,
  ]);

  const onLogoutClick = async () => {
    const logoutSuccessful = await userAccountController.logout()
    if (logoutSuccessful) {
      alertController.displayAlert(
        "Logout successful",
        null,
        "success",
        4000
      )
      window.location.replace("/latest");
      return
    }
    alertController.displayAlert(
      "Something went wrong",
      "Please contact support",
      "error",
      4000
    )
  }

  const onSaveClick = async () => {
    userAccountController.commitWorkingStockist();
    await userAccountController.saveStockistEdits();

    if (!!userAccountController.workingStockist) {
      alertController.displayAlert(
        "Changes saved successfully",
        null,
        "success",
        4000
      )
      return
    }
    alertController.displayAlert(
      "Something went wrong",
      "Please contact support",
      "error",
      3000
    )
  }

  return (
    <>
      {userAccountController && !!userAccountController.workingStockist &&
        <div className="flex flex-grow flex-col gap-4 justify-start items-center animate-fadeIn h-full">
          <Typography variant={"h3"} color={"white"}>Edit your profile</Typography>
          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"Stockist Name"}
              value={userAccountController.workingStockist.stockistName}
              onChange={event => userAccountController.onStockistNameChange(event.target.value)}
              error={userAccountController.stockistNameInputError}
            />
          </div>
          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"Email"}
              value={userAccountController.workingStockist.email}
              className={"hover:cursor-none pointer-events-none user-select-none read-only:bg-gray-600"}
              readOnly={true}
            />
          </div>
          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"Activated"}
              value={userAccountController.workingStockist.activated ? "Yes" : "Awaiting approval"}
              className={"hover:cursor-none pointer-events-none user-select-none read-only:bg-gray-600"}
              readOnly={true}
            />
          </div>
          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"Contact First Name"}
              value={userAccountController.workingStockist.contactFirstName}
              onChange={event => userAccountController.onContactFirstNameChange(event.target.value)}
              error={userAccountController.contactFirstNameInputError}
            />
          </div>
          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"Contact Surname"}
              value={userAccountController.workingStockist.contactSurname}
              onChange={event => userAccountController.onContactSurnameChange(event.target.value)}
              error={userAccountController.contactSurnameNameInputError}
            />
          </div>
          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"Contact Number"}
              value={userAccountController.workingStockist.contactTel}
              onChange={event => userAccountController.onContactNumberChange(event.target.value)}
              error={userAccountController.contactNumberInputError}
            />
          </div>
          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"VAT Number"}
              value={userAccountController.workingStockist.vatNo}
              onChange={event => userAccountController.onVatNumberChange(event.target.value)}
              error={userAccountController.vatNumberInputError}
            />
          </div>

          <div className="w-[300px] md:w-[400px] h-[50px]">
            <Input
              size={"lg"}
              color={"white"}
              label={"EORI Number"}
              className="disabled:bg-gray-600"
              value={userAccountController.workingStockist.eori}
              onChange={event => userAccountController.onEoriNumberChange(event.target.value)}
              error={userAccountController.eoriNumberInputError}
              required={userAccountController.isEoriRequired}
              disabled={!userAccountController.isEoriRequired && !userAccountController.workingStockist.eori}
            />
          </div>
          <Button
            size={"lg"}
            className={"w-[300px] md:w-[400px] h-[60px] bg-purple"}
            onClick={onSaveClick}
            disabled={userAccountController.saveButtonDisabled}
          >
            <Typography variant={"h5"}>Save</Typography>
          </Button>
          <Button
            size={"lg"}
            color={"deep-purple"}
            variant={"outlined"}
            className={"w-[300px] md:w-[400px] h-[60px]"}
            onClick={togglePasswordDialog}
          >
            <Typography variant={"h6"} color={"white"}>Change Password</Typography>
          </Button>
          <Button
            size={"md"}
            color={"red"}
            variant={"outlined"}
            className={"w-[150px] md:w-[200px] h-[45px]"}
            onClick={onLogoutClick}
          >
            <Typography variant={"small"} color={"white"}>Logout</Typography>
          </Button>
        </div>
      }

      <Dialog
        open={passwordDialogOpen}
        dismiss={{ outsidePress: false, escapeKey: true }}
        handler={togglePasswordDialog} className="bg-main/90 text-light-grey border border-light-grey rounded-none md:!rounded-xl shadow-2xl p-2 md:p-6
      max-h-[800px] m-0 md:m-4 max-w-full md:!max-w-[680px] lg:!max-w-[800px] xl:!max-w-[1000px] 2xl:!max-w-[1280px] !overflow-y-hidden
      flex flex-col">
        <DialogHeader
          color="white"
          className="text-white text-2xl border-b border-gray-600"
        >Change Password</DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <Input
            type="password"
            label="New Password"
            color="white"
            size="lg"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <Input
            type="password"
            label="Confirm Password"
            color="white"
            size="lg"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {passwordError && <Typography color="red" className="text-sm">{passwordError}</Typography>}
        </DialogBody>
        <DialogFooter className="flex justify-end gap-2 border-t border-gray-600">
          <Button variant="text" color="white" onClick={togglePasswordDialog}>Cancel</Button>
          <Button
            variant="filled"
            color="purple"
            disabled={!newPassword || !confirmPassword}
            className="disabled:bg-gray-600 bg-purple"
            onClick={onChangePasswordClick}>Confirm</Button>
        </DialogFooter>
      </Dialog>
    </>
  )
})
